import { useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2,
  ShieldCheck,
  ShieldAlert,
  ShieldBan,
  AlertTriangle,
  Pencil,
  Save,
  X,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useCoopModerationRules, useUpdateCoopModerationRule } from '../api';
import type { ModerationRule } from '../api';
import { Button } from '@/components/ui/button';
import { useQueryClient } from '@tanstack/react-query';

function severityColor(severity: string): string {
  switch (severity) {
    case 'critical':
      return 'text-red-600 dark:text-red-400';
    case 'high':
      return 'text-orange-600 dark:text-orange-400';
    case 'medium':
      return 'text-yellow-600 dark:text-yellow-400';
    case 'low':
      return 'text-blue-600 dark:text-blue-400';
    default:
      return 'text-muted-foreground';
  }
}

function severityBadgeVariant(
  severity: string,
): 'destructive' | 'secondary' | 'default' | 'outline' {
  switch (severity) {
    case 'critical':
      return 'destructive';
    case 'high':
      return 'destructive';
    case 'medium':
      return 'secondary';
    default:
      return 'outline';
  }
}

function actionIcon(action: string) {
  switch (action) {
    case 'reject':
      return <ShieldBan className="size-4 text-red-500" />;
    case 'escalate':
      return <ShieldAlert className="size-4 text-amber-500" />;
    case 'approve':
      return <ShieldCheck className="size-4 text-green-500" />;
    default:
      return <AlertTriangle className="size-4 text-muted-foreground" />;
  }
}

/** Map action_type to a concrete description of what happens to the content/user. */
function enforcementLabel(rule: ModerationRule): string {
  // Use the backend-provided description if available
  if (rule.enforcement_description) return rule.enforcement_description;

  // Fallback: infer from action_type + severity
  switch (rule.action_type) {
    case 'reject':
      switch (rule.severity) {
        case 'critical':
          return 'Hide content immediately, notify creator, flag account for review';
        case 'high':
          return 'Hide content, send policy violation warning to creator';
        case 'medium':
          return 'Hide content, notify creator with appeal option';
        default:
          return 'Hide content pending review';
      }
    case 'escalate':
      return 'Hold content and route it to human review for a decision';
    case 'approve':
      return 'Publish content immediately, no restrictions';
    default:
      return rule.action_type;
  }
}

function RuleCard({ rule }: { rule: ModerationRule }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editThreshold, setEditThreshold] = useState(
    rule.threshold != null ? String(Math.round(rule.threshold * 100)) : '',
  );
  const [editEnabled, setEditEnabled] = useState(rule.enabled);
  const updateRule = useUpdateCoopModerationRule();
  const queryClient = useQueryClient();

  function startEditing() {
    setEditThreshold(rule.threshold != null ? String(Math.round(rule.threshold * 100)) : '');
    setEditEnabled(rule.enabled);
    setIsEditing(true);
  }

  async function saveChanges() {
    const newThreshold = editThreshold ? Number(editThreshold) / 100 : undefined;
    try {
      await updateRule.mutateAsync({
        rule_id: rule.rule_id,
        threshold: newThreshold,
        enabled: editEnabled,
      });
      toast.success(`Rule "${rule.rule_name}" updated.`);
      void queryClient.invalidateQueries({ queryKey: ['moderation', 'coop', 'rules'] });
      setIsEditing(false);
    } catch {
      toast.error('Failed to update rule.');
    }
  }

  return (
    <Card className={cn(!rule.enabled && !isEditing && 'opacity-50')}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            {actionIcon(rule.action_type)}
            <div className="min-w-0 space-y-1.5">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-medium">{rule.rule_name}</h3>
                <Badge
                  variant={severityBadgeVariant(rule.severity)}
                  className="text-[10px] px-1.5 py-0"
                >
                  {rule.severity}
                </Badge>
                {!rule.enabled && !isEditing && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                    Disabled
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{rule.description}</p>

              {/* Enforcement action — what actually happens */}
              <div className="rounded bg-muted/50 px-2.5 py-1.5">
                <p className="text-xs">
                  <span className="font-medium text-muted-foreground">When triggered: </span>
                  <span>{enforcementLabel(rule)}</span>
                </p>
              </div>

              {/* Threshold — editable */}
              {isEditing ? (
                <div className="flex items-center gap-3 pt-1">
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-muted-foreground whitespace-nowrap">
                      Threshold:
                    </label>
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        value={editThreshold}
                        onChange={(e) => setEditThreshold(e.target.value)}
                        className="h-7 w-16 text-xs"
                      />
                      <span className="text-xs text-muted-foreground">%</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-muted-foreground">Enabled:</label>
                    <button
                      type="button"
                      onClick={() => setEditEnabled(!editEnabled)}
                      className={cn(
                        'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors',
                        editEnabled ? 'bg-primary' : 'bg-muted',
                      )}
                    >
                      <span
                        className={cn(
                          'pointer-events-none inline-block size-4 rounded-full bg-white shadow transition-transform',
                          editEnabled ? 'translate-x-4' : 'translate-x-0',
                        )}
                      />
                    </button>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="default"
                      className="h-7 px-2 text-xs"
                      onClick={() => void saveChanges()}
                      disabled={updateRule.isPending}
                    >
                      {updateRule.isPending ? (
                        <Loader2 className="size-3 animate-spin" />
                      ) : (
                        <Save className="size-3" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-xs"
                      onClick={() => setIsEditing(false)}
                    >
                      <X className="size-3" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 pt-0.5">
                  <p className="font-mono text-[11px] text-muted-foreground">
                    {rule.condition_summary}
                  </p>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-5 w-5 p-0"
                    onClick={startEditing}
                    title="Edit threshold"
                  >
                    <Pencil className="size-3 text-muted-foreground" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stats row */}
        {!isEditing &&
          (rule.triggers_today != null ||
            rule.triggers_total != null ||
            rule.false_positive_rate != null) && (
            <div className="mt-3 flex gap-4 border-t pt-3 text-xs text-muted-foreground">
              {rule.triggers_today != null && (
                <span>
                  Today: <strong className="text-foreground">{rule.triggers_today}</strong>
                </span>
              )}
              {rule.triggers_total != null && (
                <span>
                  Total:{' '}
                  <strong className="text-foreground">
                    {rule.triggers_total.toLocaleString()}
                  </strong>
                </span>
              )}
              {rule.false_positive_rate != null && (
                <span>
                  FP Rate:{' '}
                  <strong
                    className={cn(
                      'text-foreground',
                      rule.false_positive_rate > 10 && 'text-amber-600',
                    )}
                  >
                    {rule.false_positive_rate.toFixed(1)}%
                  </strong>
                </span>
              )}
            </div>
          )}
      </CardContent>
    </Card>
  );
}

export function ModerationRulesPanel() {
  const { data: rules, isLoading, isError, refetch } = useCoopModerationRules();

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 py-12">
        <p className="text-sm text-destructive">Failed to load moderation rules.</p>
        <Button size="sm" variant="outline" onClick={() => void refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  if (!rules || rules.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No moderation rules configured.
      </p>
    );
  }

  // Group by severity
  const critical = rules.filter((r) => r.severity === 'critical');
  const high = rules.filter((r) => r.severity === 'high');
  const medium = rules.filter((r) => r.severity === 'medium');
  const low = rules.filter((r) => r.severity === 'low');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium">Moderation Rules</h2>
          <p className="text-sm text-muted-foreground">
            Active rules evaluated by the pipeline. Click the pencil icon to edit thresholds.
          </p>
        </div>
        <Badge variant="outline" className="text-xs">
          {rules.filter((r) => r.enabled).length} active / {rules.length} total
        </Badge>
      </div>

      {critical.length > 0 && <RuleGroup label="Critical" rules={critical} />}
      {high.length > 0 && <RuleGroup label="High" rules={high} />}
      {medium.length > 0 && <RuleGroup label="Medium" rules={medium} />}
      {low.length > 0 && <RuleGroup label="Low" rules={low} />}
    </div>
  );
}

function RuleGroup({ label, rules }: { label: string; rules: ModerationRule[] }) {
  return (
    <div className="space-y-2">
      <h3
        className={cn(
          'text-xs font-semibold uppercase tracking-wide',
          severityColor(label.toLowerCase()),
        )}
      >
        {label} Severity ({rules.length})
      </h3>
      <div className="grid gap-2">
        {rules.map((rule) => (
          <RuleCard key={rule.rule_id} rule={rule} />
        ))}
      </div>
    </div>
  );
}
